<template>
  <div style="height: 100%">
    <div class="app-shell" ref="appShell">
    <!-- TOOLBAR -->
    <header class="toolbar">
      <div class="toolbar-left">
        <div class="toolbar-logo">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
        </div>
        <span class="toolbar-brand">Ký số</span>
      </div>
      <div class="toolbar-center">
        <!-- Mode Selector -->
        <div class="mode-toggle">
          <button
            class="mode-btn"
            :class="{ active: signingMode === 'KSPL' }"
            @click="signingMode = 'KSPL'"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" style="width:14px;height:14px"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
            Ký pháp lý
          </button>
          <button
            class="mode-btn"
            :class="{ active: signingMode === 'BCY' }"
            @click="signingMode = 'BCY'"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" style="width:14px;height:14px"><path fill-rule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
            Ký Ban cơ yếu
          </button>
        </div>
      </div>
      <div class="toolbar-right">
        <div v-if="pdfFileName" class="file-badge">
          <svg viewBox="0 0 20 20" fill="currentColor" style="width:13px;height:13px"><path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd"/></svg>
          {{ pdfFileName }}
        </div>
        <div v-if="signingMode === 'BCY'" class="conn-badge" :class="{ connected: certInfo }">
          <span class="conn-dot"></span>
          {{ certInfo ? 'Đã kết nối' : 'Chưa kết nối' }}
        </div>
        <div v-else class="conn-badge">
          <span style="font-size:11px;color:#64748b">Ký pháp lý</span>
        </div>
      </div>
    </header>

    <!-- SPLIT PANELS -->
    <div class="panels">

      <!-- LEFT: PDF Viewer -->
      <div class="viewer-panel">
        <!-- Empty state: dropzone -->
        <div v-if="!pdfBase64" class="viewer-empty" @click="$refs.pdfInput.click()" @dragover.prevent @drop.prevent="onDrop">
          <input ref="pdfInput" type="file" accept="application/pdf" class="sr-only" @change="onPdfSelected">
          <div class="drop-content">
            <div class="drop-icon-ring">
              <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M28 8H12a4 4 0 00-4 4v24a4 4 0 004 4h24a4 4 0 004-4V20L28 8z"/><polyline points="28,8 28,20 40,20"/></svg>
            </div>
            <h2>Kéo thả hoặc nhấn để chọn file PDF</h2>
            <p>Hỗ trợ file .pdf &mdash; Tối đa 50 MB</p>
          </div>
        </div>

        <!-- PDF loaded: interactive editor (cả 2 mode dùng chung UI) -->
        <PdfSignatureEditor
          v-else
          ref="pdfEditor"
          :pdf-base64="pdfBase64"
          :signature-image="signatureImageBase64"
          :signatures="signatures"
          :selected-sig-id="selectedSigId"
          @change-file="onPdfSelected"
          @add-signature="addSignature"
          @update-signature="updateSignature"
          @delete-signature="deleteSignature"
          @select-signature="selectSignature"
        />
      </div>

      <!-- RIGHT: Control Panel -->
      <div class="control-panel">
        <div class="control-scroll">

          <!-- Tab Bar -->
          <div class="tab-bar">
            <button
              class="tab-btn"
              :class="{ active: activeTab === 'settings' }"
              @click="activeTab = 'settings'"
            >
              <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd"/></svg>
              Thiết lập
            </button>
            <button
              class="tab-btn"
              :class="{ active: activeTab === 'signatures' }"
              @click="activeTab = 'signatures'"
            >
              <svg viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
              Chữ ký
              <span v-if="signatures.length > 0" class="tab-badge">{{ signatures.length }}</span>
            </button>
          </div>

          <!-- TAB: SETTINGS -->
          <div v-show="activeTab === 'settings'">
            <!-- Section 1: Certificate (chỉ hiện khi ký Ban cơ yếu) -->
            <section v-if="signingMode === 'BCY'" class="ctrl-section">
              <SignProviderSelector @cert-loaded="onCertLoaded" @log="addLog" />
            </section>

            <!-- KSPL mode info -->
            <section v-if="signingMode === 'KSPL'" class="ctrl-section">
              <div class="empty-hint" style="background:#f0fdf4;border-color:#bbf7d0;">
                <svg viewBox="0 0 20 20" fill="#059669" style="width:16px;height:16px;flex-shrink:0"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                <span style="color:#166534">Ký pháp lý sử dụng chứng thư số từ nhà cung cấp CA thương mại (VNPT-CA, Viettel-CA, FPT-CA...). Đặt vị trí chữ ký rồi bấm ký.</span>
              </div>
            </section>

            <!-- Section 2: Signature Image -->
            <section class="ctrl-section">
              <SignatureUploader v-model="signatureImageBase64" @log="addLog" @type-change="onSignTypeChange" />
            </section>

            <!-- Section 3: Selected Signature Position -->
            <section v-if="selectedSignature" class="ctrl-section">
              <SignaturePosition :value="selectedSignature" @input="onPositionInput" />
            </section>
            <!-- Section 3b: Text content editing -->
            <section v-if="selectedSignature && selectedSignature.type === 'text'" class="ctrl-section">
              <div class="section-label">
                <svg viewBox="0 0 24 24" fill="currentColor" style="width:15px;height:15px;color:#e67e22"><path d="M5 4v3h5.5v12h3V7H19V4z"/></svg>
                Nội dung văn bản
              </div>
              <textarea
                class="text-input"
                :value="selectedSignature.textContent || ''"
                @input="onPositionInput({ textContent: $event.target.value })"
                placeholder="Vi du: So: 01/2026/QD-UBND"
                rows="2"
              ></textarea>
              <div class="text-font-row">
                <label class="text-font-label">Cỡ chữ (pt):</label>
                <input
                  type="number"
                  class="text-font-input"
                  :value="selectedSignature.fontSize || 13"
                  @input="onPositionInput({ fontSize: parseInt($event.target.value) || 13 })"
                  min="8"
                  max="72"
                >
              </div>
              <button class="delete-text-btn" @click="deleteSignature(selectedSignature.id)">
                <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v8a2 2 0 002 2h4a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 007 2H9zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"/></svg>
                Xóa văn bản này
              </button>
            </section>
            <section v-if="!selectedSignature" class="ctrl-section">
              <div class="empty-hint">
                <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>
                <span>Chọn một chữ ký để chỉnh sửa vị trí</span>
              </div>
            </section>

            <!-- Section 4: Sign -->
            <section class="ctrl-section ctrl-section--sign">
              <div class="sign-checks">
                <div v-if="signingMode === 'BCY'" class="chk" :class="{ ok: certInfo }">
                  <svg viewBox="0 0 16 16" fill="currentColor"><path v-if="certInfo" d="M13.485 3.929a1 1 0 010 1.414l-6.364 6.364a1 1 0 01-1.414 0L3.05 9.05a1 1 0 111.414-1.414l2.05 2.05 5.657-5.657a1 1 0 011.414 0z"/><circle v-else cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
                  Chứng thư số
                </div>
                <div class="chk" :class="{ ok: pdfBase64 }">
                  <svg viewBox="0 0 16 16" fill="currentColor"><path v-if="pdfBase64" d="M13.485 3.929a1 1 0 010 1.414l-6.364 6.364a1 1 0 01-1.414 0L3.05 9.05a1 1 0 111.414-1.414l2.05 2.05 5.657-5.657a1 1 0 011.414 0z"/><circle v-else cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
                  File PDF
                </div>
                <div class="chk" :class="{ ok: signatures.length > 0 }">
                  <svg viewBox="0 0 16 16" fill="currentColor"><path v-if="signatures.length > 0" d="M13.485 3.929a1 1 0 010 1.414l-6.364 6.364a1 1 0 01-1.414 0L3.05 9.05a1 1 0 111.414-1.414l2.05 2.05 5.657-5.657a1 1 0 011.414 0z"/><circle v-else cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
                  Chữ ký ({{ signatures.length }})
                </div>
              </div>
              <!-- Cảnh báo text trống -->
              <div v-if="emptyTextSignatures.length > 0" class="text-warning">
                <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                {{ emptyTextSignatures.length }} văn bản chưa nhập nội dung
              </div>
              <button class="sign-btn" :disabled="!canSign || signing" @click="executeSign">
                <span v-if="signing" class="spinner"></span>
                <svg v-else viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
                {{ signing ? 'Đang ký số...' : (signingMode === 'BCY' ? 'Ký số Ban cơ yếu' : 'Ký số pháp lý') }}
              </button>
            </section>

            <!-- Section 5: Result -->
            <section v-if="signResult || signError" class="ctrl-section">
              <SignResult :result="signResult" :error="signError" @download="onDownload" />
            </section>
          </div>

          <!-- TAB: SIGNATURES LIST -->
          <div v-show="activeTab === 'signatures'">
            <!-- Quick add button -->
            <div class="sig-list-header">
              <span class="sig-list-title">Danh sách chữ ký</span>
              <button class="sig-add-btn" @click="addSignature(editorCurrentPage)" :disabled="!pdfBase64">
                <svg viewBox="0 0 16 16" fill="currentColor"><path fill-rule="evenodd" d="M8 2a1 1 0 011 1v4h4a1 1 0 110 2H9v4a1 1 0 11-2 0V9H3a1 1 0 010-2h4V3a1 1 0 011-1z" clip-rule="evenodd"/></svg>
                Thêm
              </button>
            </div>

            <!-- Empty state -->
            <div v-if="signatures.length === 0" class="sig-empty">
              <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
              <p>Thêm chữ ký để bắt đầu</p>
              <button class="sig-empty-btn" @click="addSignature(editorCurrentPage)" :disabled="!pdfBase64">
                <svg viewBox="0 0 16 16" fill="currentColor"><path fill-rule="evenodd" d="M8 2a1 1 0 011 1v4h4a1 1 0 110 2H9v4a1 1 0 11-2 0V9H3a1 1 0 010-2h4V3a1 1 0 011-1z" clip-rule="evenodd"/></svg>
                Thêm chữ ký đầu tiên
              </button>
            </div>

            <!-- Grouped by page -->
            <div v-else class="sig-groups">
              <div v-for="group in signaturesByPage" :key="group.page" class="sig-group">
                <div class="sig-group-header" @click="goToPage(group.page)">
                  <span class="sig-group-page">Trang {{ group.page }}</span>
                  <span class="sig-group-count">{{ group.signatures.length }} chữ ký</span>
                  <svg viewBox="0 0 16 16" fill="currentColor" class="sig-group-goto"><path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 01.708 0l6 6a.5.5 0 010 .708l-6 6a.5.5 0 01-.708-.708L10.293 8 4.646 2.354a.5.5 0 010-.708z"/></svg>
                </div>
                <div
                  v-for="sig in group.signatures"
                  :key="sig.id"
                  class="sig-item"
                  :class="{ active: selectedSigId === sig.id }"
                  @click="selectAndGoToSignature(sig)"
                >
                  <div class="sig-item-icon" :class="{ 'sig-item-icon--text': sig.type === 'text' }">
                    <svg v-if="sig.type === 'text'" viewBox="0 0 20 20" fill="currentColor"><path d="M4.167 3.333v2.5h4.583v10h2.5v-10h4.583v-2.5z"/></svg>
                    <svg v-else-if="sig.imageBase64 || signatureImageBase64" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"/></svg>
                    <svg v-else viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
                  </div>
                  <div class="sig-item-info">
                    <span class="sig-item-name">{{ sig.type === 'text' ? 'Văn bản' : 'Chữ ký' }} #{{ getGlobalIndex(sig.id) }}</span>
                    <span class="sig-item-pos">{{ sig.x }}, {{ sig.y }} &middot; {{ sig.width }}x{{ sig.height }} pt</span>
                  </div>
                  <button class="sig-item-del" @click.stop="deleteSignature(sig.id)" title="Xóa">
                    <svg viewBox="0 0 16 16" fill="currentColor"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v8a2 2 0 002 2h4a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 007 2H9zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- Summary -->
            <div v-if="signatures.length > 0" class="sig-summary">
              Tổng cộng: {{ signatures.length }} chữ ký trên {{ uniquePageCount }} trang
            </div>
          </div>

          <!-- Log toggle -->
          <button class="log-toggle" @click="showLog = !showLog">
            <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414zM11 12a1 1 0 100 2h3a1 1 0 100-2h-3z" clip-rule="evenodd"/></svg>
            Terminal ({{ logs.length }})
            <svg :class="{ flip: showLog }" viewBox="0 0 20 20" fill="currentColor" class="chevron"><path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
          </button>

          <!-- Log panel -->
          <div v-show="showLog" class="log-panel">
            <div class="log-bar">
              <span>Log</span>
              <button @click="logs = []">Xóa</button>
            </div>
            <div class="log-body" ref="logBody">
              <div v-for="(log, i) in logs" :key="i" class="lg" :class="{ 'lg-err': log.text && log.text.includes('LOI'), 'lg-ok': log.text && log.text.includes('OK') }">
                <span class="lg-t">{{ log.time }}</span> {{ log.text }}
              </div>
              <div v-if="!logs.length" class="lg-empty">Chưa có log</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Notification Modal -->
    <div v-if="modalMsg" class="modal-overlay" @click="modalMsg = null">
      <div class="modal-box" @click.stop>
        <div class="modal-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="#e67e22" stroke-width="2" width="32" height="32">
            <path d="M12 9v2m0 4h.01M10.29 3.86l-8.6 14.86A2 2 0 003.4 21h17.2a2 2 0 001.71-2.98l-8.6-14.86a2 2 0 00-3.42 0z"/>
          </svg>
        </div>
        <p class="modal-text">{{ modalMsg }}</p>
        <button class="modal-btn" @click="modalMsg = null">Đã hiểu</button>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import appConfig from "@/app.config.json";
import PageHeader from "@/components/page-header";
import SignProviderSelector from '@/components/vgcaSign/SignProviderSelector.vue'
import SignatureUploader from '@/components/vgcaSign/SignatureUploader.vue'
import SignaturePosition from '@/components/vgcaSign/SignaturePosition.vue'
import SignResult from '@/components/vgcaSign/SignResult.vue'
import PdfSignatureEditor from '@/components/vgcaSign/PdfSignatureEditor.vue'
import {CURRENT_USER} from '@/helpers/currentUser'
import vgcaService from '@/services/vgcaService'
import vgcaApiService from '@/services/vgcaApiService'

export default {
  name: 'ThietLapKySoPhapLyPage',
  page: {
    title: "Thiết lập ký số",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {  SignProviderSelector, SignatureUploader, SignaturePosition, SignResult, PdfSignatureEditor },

  mounted() {
    // Thêm class vào body để CSS override layout (chỉ page này)
    document.body.classList.add('vgca-sign-page-active')
  },
  beforeDestroy() {
    document.body.classList.remove('vgca-sign-page-active')
  },

  data() {
    return {
      title: "Thiết lập ký số pháp lý",
      items: [
        { text: "Trang chủ", href: "/" },
        { text: "Thiết lập ký số pháp lý", active: true },
      ],
      // Mode: 'KSPL' = Ký pháp lý, 'BCY' = Ký Ban cơ yếu
      signingMode: 'BCY',
      certInfo: null,
      pdfBase64: null,
      pdfFileName: '',
      uploadedFileInfo: null, // file info từ server (cho KSPL)
      signatureImageBase64: null,
      // Default size from signature type
      defaultSigWidth: 200,
      defaultSigHeight: 80,
      // Multi-signature data
      signatures: [],
      nextSigId: 1,
      selectedSigId: null,
      // UI state
      activeTab: 'settings',
      signing: false,
      signResult: null,
      signError: null,
      logs: [],
      showLog: false,
      modalMsg: null
    }
  },

  computed: {
    canSign() {
      if (this.signingMode === 'BCY') {
        if (!this.certInfo || !this.pdfBase64 || this.signatures.length === 0) return false
      } else {
        // KSPL: cần PDF + ít nhất 1 chữ ký
        if (!this.pdfBase64 || this.signatures.length === 0) return false
      }
      var hasEmptyText = this.signatures.some(function(s) {
        return s.type === 'text' && (!s.textContent || !s.textContent.trim())
      })
      return !hasEmptyText
    },

    emptyTextSignatures() {
      return this.signatures.filter(function(s) {
        return s.type === 'text' && (!s.textContent || !s.textContent.trim())
      })
    },

    selectedSignature() {
      if (this.selectedSigId === null) return null
      var self = this
      return this.signatures.find(function(s) { return s.id === self.selectedSigId }) || null
    },

    signaturesByPage() {
      var groups = {}
      this.signatures.forEach(function(sig) {
        if (!groups[sig.page]) {
          groups[sig.page] = { page: sig.page, signatures: [] }
        }
        groups[sig.page].signatures.push(sig)
      })
      var pages = Object.keys(groups).map(Number).sort(function(a, b) { return a - b })
      return pages.map(function(p) { return groups[p] })
    },

    uniquePageCount() {
      var pages = {}
      this.signatures.forEach(function(s) { pages[s.page] = true })
      return Object.keys(pages).length
    },

    editorCurrentPage() {
      if (this.$refs.pdfEditor) {
        return this.$refs.pdfEditor.currentPage
      }
      return 1
    }
  },

  methods: {
    onCertLoaded(certInfo) { this.certInfo = certInfo },

    onPdfSelected(event) {
      var file = event.target ? event.target.files[0] : null
      if (!file) return
      if (file.type !== 'application/pdf') {
        this.addLog('[PDF] LỖI: Vui lòng chọn file PDF')
        return
      }
      this.pdfFileName = file.name
      this.addLog('[PDF] Đang đọc file: ' + file.name + ' (' + (file.size / 1024).toFixed(1) + ' KB)')
      var self = this
      // Đọc base64 cho PdfSignatureEditor (BCY mode)
      var reader = new FileReader()
      reader.onload = function(e) {
        self.pdfBase64 = e.target.result.split(',')[1]
        self.addLog('[PDF] OK - File đã sẵn sàng')
      }
      reader.readAsDataURL(file)
      this.uploadFileToServer(file)
    },

    onDrop(e) {
      var file = e.dataTransfer.files[0]
      if (file && file.type === 'application/pdf') {
        this.pdfFileName = file.name
        this.addLog('[PDF] Đang đọc file: ' + file.name + ' (' + (file.size / 1024).toFixed(1) + ' KB)')
        var self = this
        var reader = new FileReader()
        reader.onload = function(ev) {
          self.pdfBase64 = ev.target.result.split(',')[1]
          self.addLog('[PDF] OK - File đã sẵn sàng')
        }
        reader.readAsDataURL(file)
        this.uploadFileToServer(file)
      }
    },

    // Upload file lên server (cho API ký pháp lý cũ cần fileId)
    async uploadFileToServer(file) {
      try {
        var formData = new FormData()
        formData.append('file', file)
        var axios = (await import('axios')).default
        var token = JSON.parse(localStorage.getItem('user-token'))
        var apiUrl = process.env.VUE_APP_API_URL
        var response = await axios.post(apiUrl + 'files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data', 'Authorization': 'Bearer ' + token }
        })
        var fileData = response.data.data || response.data
        this.uploadedFileInfo = {
          fileId: fileData.id,
          fileName: fileData.fileName,
          ext: fileData.ext,
          size: fileData.size
        }
        this.addLog('[Upload] OK - FileId: ' + fileData.id)
      } catch (err) {
        console.warn('[Upload] Lỗi:', err.message)
      }
    },

    // Multi-Signature Management
    onSignTypeChange(typeDef) {
      if (typeDef) {
        this.defaultSigWidth = typeDef.width
        this.defaultSigHeight = typeDef.height
        this.addLog('[Anh] Loai: ' + typeDef.label + ' (' + typeDef.width + 'x' + typeDef.height + ')')
      }
    },

    addSignature(page, type) {
      var sigType = type || 'signature'
      var isText = sigType === 'text'
      if (!isText && !this.signatureImageBase64) {
        this.addLog('[Chu ky] Vui lòng upload ảnh chữ ký trước khi thêm')
        this.modalMsg = 'Vui lòng upload ảnh chữ ký/mộc trước khi thêm chữ ký.'
        return
      }
      var sig = {
        id: this.nextSigId++,
        page: page || 1,
        x: 350,
        y: 50,
        width: isText ? 250 : this.defaultSigWidth,
        height: isText ? 30 : this.defaultSigHeight,
        imageBase64: null,
        type: sigType,
        textContent: isText ? '' : undefined,
        fontSize: isText ? 13 : undefined
      }
      this.signatures.push(sig)
      this.selectedSigId = sig.id
      var label = isText ? 'văn bản' : 'chữ ký'
      this.addLog('[Chu ky] Thêm ' + label + ' #' + this.getGlobalIndex(sig.id) + ' tại trang ' + sig.page)
    },

    updateSignature(id, changes) {
      var idx = this.signatures.findIndex(function(s) { return s.id === id })
      if (idx === -1) return
      var updated = Object.assign({}, this.signatures[idx], changes)
      this.$set(this.signatures, idx, updated)
    },

    deleteSignature(id) {
      var idx = this.signatures.findIndex(function(s) { return s.id === id })
      if (idx === -1) return
      var globalIdx = this.getGlobalIndex(id)
      this.signatures.splice(idx, 1)
      if (this.selectedSigId === id) {
        this.selectedSigId = this.signatures.length > 0 ? this.signatures[this.signatures.length - 1].id : null
      }
      this.addLog('[Chu ky] Đã xóa chữ ký #' + globalIdx)
    },

    selectSignature(id) {
      this.selectedSigId = id
    },

    selectAndGoToSignature(sig) {
      this.selectedSigId = sig.id
      if (this.$refs.pdfEditor) {
        this.$refs.pdfEditor.goToPage(sig.page)
      }
    },

    goToPage(page) {
      if (this.$refs.pdfEditor) {
        this.$refs.pdfEditor.goToPage(page)
      }
    },

    onPositionInput(newPos) {
      if (this.selectedSigId !== null) {
        this.updateSignature(this.selectedSigId, newPos)
      }
    },

    getGlobalIndex(id) {
      var idx = this.signatures.findIndex(function(s) { return s.id === id })
      return idx + 1
    },

    // Sign Flow
    async executeSign() {
      if (this.signingMode === 'KSPL') {
        return this.executeSignKSPL()
      }
      return this.executeSignBCY()
    },

    // Ký pháp lý - gọi API cũ: POST /api/v1/SignDigital/KySoPhapLy
    // Build payload giống hệt PdfEditorTest.handleSubmitKySoPhapLy
    async executeSignKSPL() {
      this.signing = true
      this.signResult = null
      this.signError = null
      try {
        if (!this.uploadedFileInfo) {
          this.signError = 'File chưa được upload lên server. Vui lòng thử lại.'
          return
        }

        var currentUserKySo = CURRENT_USER.USER_KY_SO
        if (!currentUserKySo || !currentUserKySo.userNameKySo) {
          this.signError = 'Chưa cấu hình tài khoản ký số pháp lý. Vui lòng cập nhật trong hồ sơ cá nhân.'
          return
        }

        this.addLog('========================================')
        this.addLog('[Ký số] Bắt đầu ký số pháp lý...')

        // Build signDigitals giống PdfEditorTest.allObjects → signatureDigital.signDigitals
        // Backend ConvertStringToBase64 cần format "data:image/png;base64,..."
        var sharedImage = this.signatureImageBase64
        var withDataPrefix = function(base64) {
          if (!base64) return null
          if (base64.startsWith('data:')) return base64
          return 'data:image/png;base64,' + base64
        }
        // Lấy kích thước trang từ PdfSignatureEditor (nếu có)
        var editor = this.$refs.pdfEditor
        var pageDims = (editor && editor.pageDims && editor.pageDims[1]) || { width: 595, height: 842 }

        var pH = Math.round(pageDims.height)
        var pW = Math.round(pageDims.width)

        var signDigitals = this.signatures.map(function(s) {
          // PdfSignatureEditor: y = từ đáy (PDF coords)
          // SmartCA backend: y = từ đỉnh (screen coords), rồi tự convert d2 = H - (y + h)
          var screenY = pH - s.y - s.height

          return {
            type: 'image',
            typeKySo: s.type === 'text' ? 'COMMENT' : 'SIGN',
            x: String(s.x),
            y: String(screenY),
            width: String(s.width),
            height: String(s.height),
            widthPage: pW,
            heightPage: pH,
            page: s.page - 1, // Backend SmartCA cộng +1, nên truyền 0-indexed
            image: withDataPrefix(s.imageBase64 || sharedImage),
            imageBase64: withDataPrefix(s.imageBase64 || sharedImage),
            text: s.textContent || null,
            fontSize: s.fontSize || 13
          }
        })

        // Payload giống hệt signatureDigital trong PdfEditorTest
        var payload = {
          file: this.uploadedFileInfo,
          userName: currentUserKySo.userNameKySo,
          password: currentUserKySo.passwordKySo,
          signDigitals: signDigitals,
          scale: 1
        }

        this.addLog('[Ký số] Gửi yêu cầu ký số pháp lý...')
        var res = await this.$store.dispatch('signDigitalStore/kySoPhapLy', payload)

        if (res.resultCode === 'SUCCESS') {
          this.addLog('[Ký số] HOÀN TẤT - ' + (res.resultString || 'Ký số pháp lý thành công!'))
          this.signResult = { message: res.resultString || 'Ký số pháp lý thành công!' }
          if (res.data && res.data.content) {
            this.signResult.signedPdfBase64 = res.data.content
          }
        } else {
          this.signError = res.resultString || 'Ký số pháp lý thất bại.'
          this.addLog('[Ký số] LỖI: ' + this.signError)
        }

        this.addLog('========================================')
      } catch (err) {
        this.signError = err.message
        this.addLog('[Ký số] LỖI: ' + err.message)
      } finally {
        this.signing = false
      }
    },

    // Ký Ban cơ yếu - VGCA API + USB Token
    async executeSignBCY() {
      this.signing = true
      this.signResult = null
      this.signError = null
      try {
        this.addLog('========================================')
        this.addLog('[Ký số] Bắt đầu luồng ký số Ban cơ yếu (3 bước)...')
        this.addLog('[Ký số] Số chữ ký: ' + this.signatures.length)
        this.addLog('[Bước 1/3] Gửi file PDF + chứng thư số lên Backend...')

        var sigData = this.signatures.map(function(s) {
          return {
            x: s.x,
            y: s.y,
            page: s.page,
            width: s.width,
            height: s.height,
            imageBase64: s.imageBase64,
            type: s.type || 'signature',
            textContent: s.textContent,
            fontSize: s.fontSize
          }
        })

        var result1 = await vgcaApiService.prepareSign(
          this.pdfBase64, this.certInfo.Base64Data, this.signatureImageBase64, sigData
        )
        var sessionId = result1.sessionId
        var hashBase64 = result1.hashBase64
        this.addLog('[Bước 1/3] OK - SessionId: ' + sessionId)
        this.addLog('[Bước 1/3] OK - Hash (Base64): ' + hashBase64.substring(0, 30) + '...')
        this.addLog('[Bước 2/3] Gửi hash cho VGCASignService (USB Token)...')
        this.addLog('[Bước 2/3] Vui lòng nhập mã PIN khi được yêu cầu...')

        var signResult = await vgcaService.signHash(hashBase64)
        this.addLog('[Bước 2/3] OK - Ký hash thành công!')
        this.addLog('[Bước 2/3] OK - Signature (PKCS#7): ' + signResult.Signature.substring(0, 30) + '...')
        this.addLog('[Bước 3/3] Gửi chữ ký PKCS#7 lên Backend...')

        var result = await vgcaApiService.completeSign(sessionId, signResult.Signature)
        this.signResult = result

        // Load signed PDF into viewer
        if (result.signedPdfBase64) {
          this.pdfBase64 = result.signedPdfBase64
          this.signatures = []
          this.selectedSigId = null
          this.pdfFileName = '(đã ký) ' + this.pdfFileName
        }

        this.addLog('[Bước 3/3] OK - ' + result.message)
        this.addLog('[Ký số] HOÀN TẤT - Ký số thành công!')
        this.addLog('========================================')
      } catch (err) {
        this.signError = err.message
        this.addLog('[Ký số] LỖI: ' + err.message)
      } finally {
        this.signing = false
      }
    },

    async onDownload(fileId, fileName) {
      try {
        this.addLog('[Download] Đang tải file: ' + (fileName || 'signed.pdf') + '...')
        await vgcaApiService.downloadSigned(fileId, fileName)
        this.addLog('[Download] OK - Đã tải file')
      } catch (err) {
        this.addLog('[Download] LOI: ' + err.message)
      }
    },

    addLog(text) {
      var now = new Date()
      var time = now.toLocaleTimeString('vi-VN', { hour12: false })
      this.logs.push({ time: time, text: text })
      this.showLog = true
      var self = this
      this.$nextTick(function() {
        var el = self.$refs.logBody
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  }
}
</script>

<style scoped>
/* Fonts loaded via Google Fonts CDN */

.app-shell {
  --ink: #1e293b;
  --blue: #00568c;
  --blue-light: #e6f2f8;
  --emerald: #059669;
  --red: #dc2626;
  --gray-50: #f8fafc;
  --gray-100: #f1f5f9;
  --gray-200: #e2e8f0;
  --gray-400: #94a3b8;
  --gray-500: #64748b;
  --gray-700: #334155;
  --gray-900: #ffffff;
  font-family: 'Roboto', system-ui, sans-serif;
  /* Fill toàn bộ chiều cao có sẵn trong layout doffice */
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  background: var(--gray-100);
  color: var(--ink);
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--gray-200);
}

.sr-only { position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0); }

/* TOOLBAR */
.toolbar {
  height: 48px;
  background: #fff;
  border-bottom: 1px solid var(--gray-200);
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 16px;
  flex-shrink: 0;
  z-index: 1;
  margin-top: 10px;
}
.toolbar-left { display:flex; align-items:center; gap:10px; min-width:120px; }
.toolbar-logo {
  width: 28px; height: 28px;
  background: var(--blue);
  border-radius: 7px;
  display: flex; align-items: center; justify-content: center;
}
.toolbar-logo svg { width:16px; height:16px; color:#fff; }
.toolbar-brand { color:var(--blue); font-weight:700; font-size:14px; letter-spacing:-0.3px; }
.toolbar-center { flex:1; display:flex; align-items:center; justify-content:center; gap:6px; }
.toolbar-right { min-width:120px; display:flex; justify-content:flex-end; align-items:center; gap:10px; }

/* Mode Toggle */
.mode-toggle {
  display: flex;
  background: var(--gray-100);
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
}
.mode-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border: none;
  background: transparent;
  color: var(--gray-500);
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  white-space: nowrap;
}
.mode-btn:hover { color: var(--gray-700); background: rgba(255,255,255,0.5); }
.mode-btn.active {
  background: #fff;
  color: var(--blue);
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.file-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--gray-500);
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.conn-badge {
  display:flex; align-items:center; gap:6px;
  font-size:12px; color:var(--gray-500);
  padding:4px 12px; border-radius:999px;
  background: var(--gray-50);
  border: 1px solid var(--gray-200);
}
.conn-dot { width:7px; height:7px; border-radius:50%; background:var(--gray-400); transition:all 0.3s; }
.conn-badge.connected .conn-dot { background:#34d399; box-shadow:0 0 6px #34d399; }
.conn-badge.connected { color:var(--emerald); }

/* SPLIT PANELS */
.panels { flex:1; display:flex; overflow:hidden; }

/* LEFT: PDF VIEWER */
.viewer-panel {
  flex: 1;
  min-width: 0;
  background: var(--gray-100);
  position: relative;
  display: flex;
  overflow: hidden;
}
.viewer-empty {
  flex:1; display:flex; align-items:center; justify-content:center;
  cursor:pointer; transition: background 0.2s;
  border: 3px dashed var(--gray-200);
  margin: 16px;
  border-radius: 12px;
}
.viewer-empty:hover { background:#e8f0fe; border-color:#7abee0; }
.drop-content { text-align:center; }
.drop-icon-ring {
  width:80px; height:80px; margin:0 auto 20px;
  border-radius:50%; background:var(--blue-light);
  display:flex; align-items:center; justify-content:center;
}
.drop-icon-ring svg { width:40px; height:40px; color:var(--blue); }
.drop-content h2 { font-size:18px; font-weight:600; color:var(--gray-700); margin-bottom:6px; }
.drop-content p { font-size:13px; color:var(--gray-400); }

/* RIGHT: CONTROL PANEL */
.control-panel {
  width: 380px;
  min-width: 380px;
  flex-shrink: 0;
  background: #fff;
  border-left: 1px solid var(--gray-200);
  display: flex;
  flex-direction: column;
  z-index: 5;
}
.control-scroll {
  flex:1; overflow-y:auto; display:flex; flex-direction:column;
}

/* TAB BAR */
.tab-bar {
  display: flex;
  background: var(--gray-50);
  border-bottom: 1px solid var(--gray-200);
  flex-shrink: 0;
}
.tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 16px;
  border: none;
  background: transparent;
  color: var(--gray-500);
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
  position: relative;
}
.tab-btn svg { width: 15px; height: 15px; }
.tab-btn:hover { color: var(--gray-700); background: var(--gray-100); }
.tab-btn.active {
  color: var(--blue);
  border-bottom-color: var(--blue);
  background: #fff;
}
.tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--blue);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
}

.ctrl-section {
  padding:16px 20px;
  border-bottom:1px solid var(--gray-200);
}

/* EMPTY HINT */
.empty-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: var(--gray-50);
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  font-size: 12px;
  color: var(--gray-500);
}
.empty-hint svg { width: 16px; height: 16px; flex-shrink: 0; color: var(--gray-400); }

/* SIGN SECTION */
.sign-checks {
  display:flex; gap:12px; margin-bottom:12px; flex-wrap:wrap;
}
.chk {
  display:flex; align-items:center; gap:5px;
  font-size:12px; color:var(--gray-400);
}
.chk svg { width:14px; height:14px; }
.chk.ok { color:var(--emerald); }

/* Notification Modal */
.modal-overlay {
  position:fixed; inset:0; background:rgba(0,0,0,0.5); backdrop-filter:blur(4px);
  z-index:99999; display:flex; align-items:center; justify-content:center; padding:24px;
}
.modal-box {
  background:#fff; border-radius:14px; padding:28px 24px; text-align:center;
  max-width:380px; width:100%; box-shadow:0 20px 40px rgba(0,0,0,0.2);
  animation: modalSlideUp 0.2s ease;
}
@keyframes modalSlideUp { from{transform:translateY(12px);opacity:0} to{transform:translateY(0);opacity:1} }
.modal-icon {
  width:56px; height:56px; border-radius:50%; background:#fef3c7;
  display:flex; align-items:center; justify-content:center; margin:0 auto 16px;
}
.modal-text { font-size:14px; color:#1e293b; line-height:1.6; margin-bottom:20px; }
.modal-btn {
  padding:10px 32px; border:none; border-radius:8px; background:var(--blue);
  color:#fff; font-size:14px; font-weight:600; font-family:inherit; cursor:pointer;
  transition:background 0.2s;
}
.modal-btn:hover { background:#003d63; }

.delete-text-btn {
  width:100%; padding:8px; border:1px solid #fecaca; border-radius:6px;
  background:#fef2f2; color:#dc2626; font-size:12px; font-weight:500;
  font-family:inherit; cursor:pointer; display:flex; align-items:center;
  justify-content:center; gap:6px; margin-top:8px; transition:all 0.15s;
}
.delete-text-btn:hover { background:#fee2e2; border-color:#f87171; }

.text-warning {
  display:flex; align-items:center; gap:6px; padding:8px 12px; margin-bottom:10px;
  background:#fef3c7; border:1px solid #fbbf24; border-radius:6px;
  font-size:12px; color:#92400e; font-weight:500;
}
.text-warning svg { width:16px; height:16px; color:#f59e0b; flex-shrink:0; }

.sign-btn {
  width:100%; padding:12px; border:none; border-radius:8px;
  background: var(--blue);
  color:#fff; font-size:15px; font-weight:600; font-family:inherit;
  cursor:pointer; display:flex; align-items:center; justify-content:center; gap:8px;
  transition: all 0.25s;
}
.sign-btn:hover:not(:disabled) { box-shadow:0 6px 20px rgba(0,86,140,0.35); transform:translateY(-1px); }
.sign-btn:disabled { opacity:0.35; cursor:not-allowed; transform:none; box-shadow:none; }
.sign-btn svg { width:18px; height:18px; }
.spinner {
  width:18px; height:18px; border:2.5px solid rgba(255,255,255,0.3);
  border-top-color:#fff; border-radius:50%; animation:spin 0.6s linear infinite;
}
@keyframes spin { to{transform:rotate(360deg)} }

/* SIGNATURES TAB */
.sig-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid var(--gray-200);
}
.sig-list-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--gray-700);
}
.sig-add-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border: 1px solid var(--blue);
  border-radius: 5px;
  background: var(--blue-light);
  color: var(--blue);
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
}
.sig-add-btn:hover { background: #cce5f0; }
.sig-add-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.sig-add-btn svg { width: 14px; height: 14px; }

/* Empty state */
.sig-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  color: var(--gray-400);
}
.sig-empty svg { width: 48px; height: 48px; margin-bottom: 12px; color: var(--gray-200); }
.sig-empty p { font-size: 14px; margin-bottom: 16px; }
.sig-empty-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  border: 1px dashed var(--gray-200);
  border-radius: 8px;
  background: var(--gray-50);
  color: var(--gray-500);
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s;
}
.sig-empty-btn:hover { border-color: var(--blue); color: var(--blue); background: var(--blue-light); }
.sig-empty-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.sig-empty-btn svg { width: 14px; height: 14px; }

/* Groups */
.sig-groups { flex: 1; }
.sig-group { border-bottom: 1px solid var(--gray-200); }
.sig-group-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: var(--gray-50);
  cursor: pointer;
  transition: background 0.15s;
}
.sig-group-header:hover { background: var(--gray-100); }
.sig-group-page { font-size: 12px; font-weight: 700; color: var(--gray-700); }
.sig-group-count { font-size: 11px; color: var(--gray-400); flex: 1; }
.sig-group-goto { width: 12px; height: 12px; color: var(--gray-400); }

.sig-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 20px 8px 32px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid var(--gray-100);
}
.sig-item:last-child { border-bottom: none; }
.sig-item:hover { background: var(--gray-50); }
.sig-item.active { background: var(--blue-light); }

.sig-item-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: var(--gray-100);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.sig-item-icon svg { width: 14px; height: 14px; color: var(--gray-500); }
.sig-item.active .sig-item-icon { background: rgba(0,86,140,0.15); }
.sig-item.active .sig-item-icon svg { color: var(--blue); }

.sig-item-info { flex: 1; min-width: 0; }
.sig-item-name {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--gray-700);
}
.sig-item-pos {
  display: block;
  font-size: 11px;
  color: var(--gray-400);
  font-family: 'JetBrains Mono', monospace;
}

.sig-item-del {
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: var(--gray-400);
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.15s;
  flex-shrink: 0;
}
.sig-item-del svg { width: 14px; height: 14px; }
.sig-item:hover .sig-item-del { opacity: 1; }
.sig-item-del:hover { background: #fef2f2; color: var(--red); }

.sig-item-icon--text { background: rgba(230,126,34,0.1); }
.sig-item-icon--text svg { color: #e67e22 !important; }

.sig-summary {
  padding: 10px 20px;
  font-size: 11px;
  color: var(--gray-400);
  text-align: center;
  border-top: 1px solid var(--gray-200);
  background: var(--gray-50);
}

/* LOG TOGGLE + PANEL */
.log-toggle {
  display:flex; align-items:center; gap:6px;
  padding:10px 20px; border:none; background:var(--gray-50);
  color:var(--gray-500); font-size:12px; font-weight:600; font-family:inherit;
  cursor:pointer; border-top:1px solid var(--gray-200); width:100%;
  text-align:left;
  margin-top: auto;
}
.log-toggle svg { width:16px; height:16px; }
.log-toggle:hover { background:var(--gray-100); }
.chevron { margin-left:auto; transition:transform 0.2s; }
.chevron.flip { transform:rotate(180deg); }

.log-panel {
  background:var(--gray-900);
  flex-shrink:0;
}
.log-bar {
  display:flex; justify-content:space-between; align-items:center;
  padding:6px 12px; border-bottom:1px solid #1e293b;
  font-size:11px; color:var(--gray-500);
}
.log-bar button {
  padding:2px 8px; background:transparent; color:var(--gray-500);
  border:1px solid #334155; border-radius:3px; font-size:10px; cursor:pointer; font-family:inherit;
}
.log-bar button:hover { color:var(--gray-400); }
.log-body {
  padding:8px 12px; max-height:200px; overflow-y:auto;
  font-family:'JetBrains Mono',monospace; font-size:11px; line-height:1.6;
}
.lg { color:#94a3b8; word-break:break-all; }
.lg-err { color:#f87171; }
.lg-ok { color:#4ade80; }
.lg-t { color:#475569; margin-right:6px; }
.lg-empty { color:#334155; text-align:center; padding:16px 0; font-style:italic; }
.log-body::-webkit-scrollbar { width:5px; }
.log-body::-webkit-scrollbar-track { background:var(--gray-900); }
.log-body::-webkit-scrollbar-thumb { background:#334155; border-radius:3px; }

/* scrollbar for control panel */
.control-scroll::-webkit-scrollbar { width:5px; }
.control-scroll::-webkit-scrollbar-track { background:transparent; }
.control-scroll::-webkit-scrollbar-thumb { background:var(--gray-200); border-radius:3px; }

/* Text Annotation Editing */
.section-label {
  display:flex; align-items:center; gap:6px;
  font-size:13px; font-weight:600; color:#334155; margin-bottom:8px;
}
.text-input {
  width:100%; padding:8px 10px; border:1px solid var(--gray-200); border-radius:6px;
  font-size:13px; font-family:inherit; resize:vertical;
  outline:none; color:#1e293b; background:#fff; box-sizing:border-box;
}
.text-input:focus { border-color:var(--blue); box-shadow:0 0 0 2px rgba(0,86,140,0.1); }
.text-font-row { display:flex; align-items:center; gap:8px; margin-top:8px; }
.text-font-label { font-size:12px; color:#64748b; white-space:nowrap; }
.text-font-input {
  width:60px; padding:4px 8px; border:1px solid var(--gray-200); border-radius:4px;
  font-size:13px; font-family:inherit; outline:none; text-align:center;
}
.text-font-input:focus { border-color:var(--blue); }

/* RESPONSIVE */
@media (max-width:900px) {
  .panels { flex-direction:column; }
  .viewer-panel { min-height:40vh; }
  .control-panel { width:100%; min-width:0; border-left:none; border-top:1px solid var(--gray-200); }
  .toolbar-center { display:none; }
  .mode-toggle { display:none; }
}
</style>

<!-- Override layout doffice chỉ khi page này active (body class toggle) -->
<style>
body.vgca-sign-page-active .page-content {
  padding-bottom: 0 !important;
}
body.vgca-sign-page-active .footer {
  display: none !important;
}
body.vgca-sign-page-active .page-content > .container-fluid {
  padding: 0 !important;
  max-width: 100% !important;
}
</style>
